import { useCallback, type ReactNode, type TouchEvent, type MouseEvent } from 'react';
import styles from '@/components/jog/jog.module.css';

export interface JogButtonProps {
  label: string;
  axisIndex: number;
  direction: 1 | -1;
  disabled: boolean;
  className?: string;
  /** Custom visual content; falls back to `label` text when omitted. */
  children?: ReactNode;
  onStart: (axisIndex: number, dir: 1 | -1) => void;
  onStop: (axisIndex: number) => void;
}

/** Hold-to-jog button: jogs while pressed, stops on release. */
export function JogButton({
  label,
  axisIndex,
  direction,
  disabled,
  className = '',
  children,
  onStart,
  onStop,
}: JogButtonProps): JSX.Element {
  const start = useCallback(() => {
    if (!disabled) onStart(axisIndex, direction);
  }, [disabled, axisIndex, direction, onStart]);
  const stop = useCallback(() => onStop(axisIndex), [axisIndex, onStop]);

  const onTouchStart = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      start();
    },
    [start],
  );
  const onTouchEnd = useCallback(
    (e: TouchEvent) => {
      e.preventDefault();
      stop();
    },
    [stop],
  );
  const onMouseDown = useCallback((_e: MouseEvent) => start(), [start]);
  const onMouseUp = useCallback((_e: MouseEvent) => stop(), [stop]);
  const onMouseLeave = useCallback((_e: MouseEvent) => stop(), [stop]);

  return (
    <button
      className={`${styles.jogBtn} ${className} ${disabled ? styles.jogBtnDisabled : ''}`}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={stop}
      type="button"
      disabled={disabled}
      aria-label={label}
    >
      {children ?? label}
    </button>
  );
}
